import { Fragment, useRef, useState } from 'react'
import { List, fromJS } from 'immutable'
import cc from 'classcat'
import loadable from '@loadable/component'

import { handleEscapeKeyDown } from '../../../../../utils/hooks/useSortableHelpers'
import { useTranslate } from '../../../../../utils/translate'
import LazyImage from '../../../../LazyImage'
import PluginPlaceholder from '../components/PluginPlaceholder/PluginPlaceholder'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './LogoSettings'))

interface LogoImage extends LinkProps {
  src: string
  width: number
  height: number
  alt: string
  position: number

  // Image edit properties
  refSrc: string
  edit: {
    aspectRatio: { label: string; value: number }
    offset: [number, number]
    zoom: number
  }
}

interface LinkProps {
  readonly link?: string
  readonly opentab?: boolean
}

export default function LogoPlugin({
  config,
  data = fromJS({
    size: 50,
    gap: 50,
    images: [],
  }),
  editorMode,
  editorView,
  onEdit,
  onCancel,
  onDataChange,
  onSave,
}: Readonly<WorkspacePluginProps>) {
  const [isBusy, setIsBusy] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const t = useTranslate('interface')

  const isEmpty = data.get('images', List()).size === 0
  const images = data
    .get('images', List())
    .toJS()
    .sort((a: LogoImage, b: LogoImage) => a.position - b.position)
    .map((image: LogoImage) => ({
      src: config?.imageUrl?.(image.src),
      width: image.width,
      height: image.height,
      alt: image.alt ?? '',
      link: image.link ?? '',
      opentab: image.opentab,
    }))

  const size = data.get('size')
  const gap = data.get('gap')

  function handleCancel() {
    setIsBusy(false)
    onCancel()
  }

  if (editorView) {
    return (
      <div
        className={cc([
          {
            'dali-plugin-logo': true,
            'dali-grid-element-highlighted': editorMode === 'edit',
            busy: isBusy,
          },
        ])}
        ref={ref}
      >
        {isBusy && (
          <div className="image-busy-indicator" data-testid="logoElementBusyIndicator">
            <span />
          </div>
        )}
        {isEmpty ? (
          <PluginPlaceholder iconClassName="plugin-placeholder-logo" onAdd={() => onEdit()}>
            {t('components.logoElementComponent.imageFieldMultiple.addImagesButton.label')}
          </PluginPlaceholder>
        ) : (
          <Logos size={size} gap={gap} images={images} />
        )}
        <SettingsLayer
          open={editorMode === 'edit'}
          virtualRef={ref}
          side="top"
          align="center"
          sideOffset={-600}
          onEscapeKeyDown={handleEscapeKeyDown(handleCancel)}
          className="logo-settingslayer"
        >
          {({ renderLayout }) => (
            <SettingsForm
              config={config}
              data={data}
              onDataChange={onDataChange}
              onSave={onSave}
              onCancel={handleCancel}
              onBusyStateChange={setIsBusy}
              renderLayout={renderLayout}
            />
          )}
        </SettingsLayer>
      </div>
    )
  }

  return isEmpty ? null : <Logos size={size} gap={gap} images={images} />
}

function Logos({ size, gap, images }: Readonly<{ size: number; gap: number; images: LogoImage[] }>) {
  return (
    <div className="dali-plugin-logo-content" style={{ '--size': size + 'px', '--gap': gap + 'px' }}>
      {images.map((image, index) => {
        const img = <LazyImage src={image.src} width={image.width} height={image.height} alt={image.alt} />

        return (
          <Fragment key={`${image.src}${index}`}>
            {image.link ? (
              <a
                href={image.link}
                target={image.opentab ? '_blank' : undefined}
                rel={image.opentab ? 'noopener noreferrer' : undefined}
              >
                {img}
              </a>
            ) : (
              img
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
